import { useCallback, useEffect, useRef, useState } from "react";
import CollaboratorsAPI from "../../../services/api/collaborators.api";
import { methods } from "../../../utils/axios";
import logo from "../../../assets/gm-logo-name.svg";
import emailIcon from "../../../assets/email_icon.png";
import timerIcon from "../../../assets/timer_icon.png";
import termIcon01 from "../../../assets/term_icon_01.png";
import termIcon02 from "../../../assets/term_icon_02.png";
import termIcon03 from "../../../assets/term_icon_03.png";
import SortableList from "../../components/sortable-list/sortable-list";
import { getUniqueRandomNumber } from "../../../utils/randomNumbers";
import {
  isSpecificCompetencyMode,
  selectCompetencySections,
  selectSpecificCompetencySections,
} from "./select-competency-sections";

const STEPS = {
  TERMS: 'TERMS',
  DISC_PHASE: 'DISC_PHASE',
  COMPETENCY_PHASE: 'COMPETENCY_PHASE',
  INTRO_ANSWER_FUNDAMENTALS: 'INTRO_ANSWER_FUNDAMENTALS',
  ANSWER_FUNDAMENTALS: 'ANSWER_FUNDAMENTALS',
  FINISHED: 'FINISHED',
};

const MAX_ANSWER_FUNDAMENTALS = 2;

// Límites de tiempo (valor por defecto de un timer recién arrancado). La autoridad
// del tiempo restante ante recarga es el servidor, que devuelve remainingSeconds.
const GLOBAL_TIME = (parseInt(process.env.REACT_APP_TEST_TIMEOUT_MINUTES, 10) || 60) * 60;
const QUESTION_TIME = parseInt(process.env.REACT_APP_QUESTION_TIMEOUT_SECONDS, 10) || 180;

const isAutoFillEnabled = () => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  try {
    return localStorage.getItem('discTestEasterEgg') === '35182a5354dfadb12e4c98f86d0479f6';
  } catch (error) {
    return false;
  }
};

const AUTOFILL_ENABLED = isAutoFillEnabled();

const UnifiedTest = (props) => {
  const { test } = props;
  const [currentStep, setCurrentStep] = useState(STEPS.TERMS);
  const [loading, setLoading] = useState(false);

  // ── DISC phase state ─────────────────────────────────────────────────────
  const discSection = test.sections?.[0]; // "Su Estilo de Trabajo"
  const [discQuestions, setDiscQuestions] = useState([]);
  const [discFormData, setDiscFormData] = useState({ '0': {} });

  // ── Competency phase state ────────────────────────────────────────────────
  const [competencyQuestions, setCompetencyQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [startTestDate, setStartTestDate] = useState(null);
  const [finishTestDate, setFinishTestDate] = useState(null);
  const [answerFundamentalsIndexs, setAnswerFundamentalsIndexs] = useState([]);
  const [currentAnswerFundamentalsIndex, setCurrentAnswerFundamentalsIndex] = useState(0);
  const [isFinalStep, setIsFinalStep] = useState(false);
  // Semilla inicial de los timers (segundos). Al hidratar viene del servidor; en flujo
  // normal se arranca con el valor completo del límite.
  const [globalInitialSeconds, setGlobalInitialSeconds] = useState(null);
  const [questionInitialSeconds, setQuestionInitialSeconds] = useState(null);
  const [isFinishTimerModalOpen, setIsFinishTimerModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: 'Se ha pasado el tiempo para responder',
    subtitle: 'No sobrepienses la respuesta, responde para continuar',
    buttonText: 'Continuar',
  });

  useEffect(() => {
    if (!test?.sections) return;

    // Sesión persistida (issue #10): rehidratar sin re-aleatorizar.
    if (test.progress) {
      hydrateFromProgress(test.progress);
      return;
    }

    // Section 0: DISC drag-and-drop
    if (discSection) {
      const qs = discSection.questions.map((question, index) => ({
        id: question.id,
        section: 0,
        options: question.options.map((opt) => ({
          id: opt.questionOptionID,
          text: `${opt.spanish.charAt().toUpperCase()}${opt.spanish.slice(1)}`,
        })),
        title: `${index + 1}. ${question.spanish}`,
        subtitle: 'Arrastre y ordene las opciones según su prioridad, de mayor a menor relevancia.',
      }));
      setDiscQuestions(qs);
    }

    // Sections 1–8: competency questions (flattened and shuffled)
    const competencySections = test.sections.slice(1);
    const allQuestions = [];
    competencySections.forEach((section) => {
      section.questions.forEach((question) => {
        allQuestions.push(question);
      });
    });
    for (let i = allQuestions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
    }
    setCompetencyQuestions(allQuestions);
  }, [test]);

  // ── Persistencia de sesión (issue #10) ──────────────────────────────────────
  const collaboratorTestID = test.collaboratorTestID;

  const hydrateFromProgress = (progress) => {
    if (progress.discQuestions) setDiscQuestions(progress.discQuestions);
    if (progress.discFormData) setDiscFormData(progress.discFormData);
    // `?.length` y no truthiness: un snapshot con `[]` es truthy y restauraría la fase
    // de competencias sin preguntas, dejando la sesión sin salida en cada recarga.
    if (progress.competencyQuestions?.length) setCompetencyQuestions(progress.competencyQuestions);
    if (progress.startTestDate) setStartTestDate(progress.startTestDate);
    if (progress.finishTestDate) setFinishTestDate(progress.finishTestDate);
    if (progress.answerFundamentalsIndexs) setAnswerFundamentalsIndexs(progress.answerFundamentalsIndexs);
    if (Number.isInteger(progress.currentAnswerFundamentalsIndex)) setCurrentAnswerFundamentalsIndex(progress.currentAnswerFundamentalsIndex);
    if (Number.isInteger(progress.currentQuestionIndex)) setCurrentQuestionIndex(progress.currentQuestionIndex);
    if (progress.isFinalStep) setIsFinalStep(progress.isFinalStep);
    // Semilla de timers desde el servidor (fuente de verdad del tiempo restante).
    // El backend devuelve estos campos en el nivel superior de la respuesta (hermanos
    // de `progress`), no dentro de `progress`.
    setGlobalInitialSeconds(test.globalRemainingSeconds != null ? test.globalRemainingSeconds : GLOBAL_TIME);
    setQuestionInitialSeconds(test.questionRemainingSeconds != null ? test.questionRemainingSeconds : QUESTION_TIME);
    // El paso se fija al final para que el resto del estado ya esté disponible.
    if (progress.currentStep) setCurrentStep(progress.currentStep);
  };

  // Construye el snapshot de progreso a partir del estado actual (con overrides para
  // los valores que se están cambiando en la misma transición, dado que setState es async).
  const buildProgress = (overrides = {}) => ({
    currentStep,
    currentQuestionIndex,
    discQuestions,
    discFormData,
    competencyQuestions,
    startTestDate,
    finishTestDate,
    answerFundamentalsIndexs,
    currentAnswerFundamentalsIndex,
    isFinalStep,
    ...overrides,
  });

  const persistProgress = (overrides = {}) => {
    CollaboratorsAPI.saveTestProgress(collaboratorTestID, buildProgress(overrides))
      .catch((error) => console.error('No se pudo guardar el progreso del test', error));
  };

  // ── DISC helpers ─────────────────────────────────────────────────────────
  const handleDiscQuestionChange = useCallback((questionId, sortedItems) => {
    setDiscFormData(prev => ({
      ...prev,
      '0': { ...prev['0'], [questionId]: sortedItems },
    }));
  }, []);

  const validateDiscForm = () => {
    const missingQuestions = [];
    discQuestions.forEach(question => {
      const questionData = discFormData['0'][question.id];
      if (!questionData || questionData.some(item => item === null)) {
        missingQuestions.push(question.title);
      }
    });
    return { isValid: missingQuestions.length === 0, missingQuestions };
  };

  // ── Competency helpers ────────────────────────────────────────────────────
  const isOptionSelected = () => {
    const question = competencyQuestions[currentQuestionIndex];
    return question?.options.some(option => option.selected);
  };

  const onChangeOption = (indexOption) => () => {
    // Copia inmutable de la pregunta y del array (no mutar el estado previo ni el snapshot).
    const updated = competencyQuestions.map((question, questionIndex) => {
      if (questionIndex !== currentQuestionIndex) return question;
      return {
        ...question,
        options: question.options.map((option, index) => {
          const { id, questionOptionID, spanish, levelOption } = option;
          const localOption = { id, questionOptionID, spanish, levelOption };
          if (index === indexOption) localOption.selected = true;
          return localOption;
        }),
      };
    });
    setCompetencyQuestions(updated);
    persistProgress({ competencyQuestions: updated });
  };

  const onChangeFundamentalInput = (e) => {
    if (e.target.value.length <= 700) {
      const targetIndex = answerFundamentalsIndexs[currentAnswerFundamentalsIndex];
      // Copia inmutable de la pregunta objetivo (no mutar el estado previo).
      const updated = competencyQuestions.map((question, questionIndex) =>
        questionIndex === targetIndex ? { ...question, answerFundamental: e.target.value } : question
      );
      setCompetencyQuestions(updated);
    }
  };

  const onFinishQuestionTime = () => {
    setModalContent({
      title: 'Se ha pasado el tiempo para responder',
      subtitle: 'No sobrepienses la respuesta, responde para continuar',
      buttonText: 'Continuar',
    });
    setIsFinishTimerModalOpen(true);
  };

  const onFinishTestTime = () => {
    setModalContent({
      title: 'Se ha agotado el tiempo máximo de la prueba',
      subtitle: 'Igualmente puedes continuar. Recuerda no sobrepensar las respuestas.',
      buttonText: 'Continuar',
    });
    setIsFinishTimerModalOpen(true);
  };

  // ── Shared helpers ─────────────────────────────────────────────────────────
  const buildDiscAnswers = () => {
    const discAnswers = [];
    Object.keys(discFormData['0'] || {}).forEach((questionID) => {
      const options = discFormData['0'][questionID];
      options.forEach((opt, idx) => {
        discAnswers.push({
          questionOptionID: opt.id,
          optionPriority: options.length - idx,
        });
      });
    });
    return discAnswers;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const submitForm = () => {
    const answers = buildDiscAnswers();

    // Competency answers (sections 1–8)
    competencyQuestions.forEach((question) => {
      question.options.forEach((option) => {
        const answer = {
          questionOptionID: option.questionOptionID,
          optionPriority: option.selected ? 1 : 0,
        };
        if (option.selected) {
          if (question.answerFundamental) answer.answerFundamental = question.answerFundamental;
          answer.startQuestionDate = question.startQuestionDate;
          answer.finishQuestionDate = question.finishQuestionDate;
        }
        answers.push(answer);
      });
    });

    const body = {
      collaboratorTestID: test.collaboratorTestID,
      startTestDate,
      finishTestDate,
      answers,
    };

    setLoading(true);
    methods.post('/collaborator_tests/post_answers', body)
      .then(() => setCurrentStep(STEPS.FINISHED))
      .catch(() => {
        alert('Ocurrió un error al enviar tus respuestas');
        setLoading(false);
      });
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const nextAction = async () => {
    if (currentStep === STEPS.TERMS) {
      setCurrentStep(STEPS.DISC_PHASE);
      // Inicia la sesión en el servidor (fija startedAt/opened, congela el snapshot DISC).
      CollaboratorsAPI.startTestSession(collaboratorTestID, buildProgress({ currentStep: STEPS.DISC_PHASE }))
        .catch((error) => console.error('No se pudo iniciar la sesión del test', error));
      window.scrollTo(0, 0);
      return;
    }

    if (currentStep === STEPS.DISC_PHASE) {
      const validation = validateDiscForm();
      if (!validation.isValid) {
        alert('Por favor complete todas las preguntas antes de continuar.\n\n' +
          'Preguntas incompletas:\n' +
          validation.missingQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n'));
        return;
      }

      const discAnswers = buildDiscAnswers();

      setLoading(true);
      try {
        // Modo "competencias específicas" (#67): las 8 se eligieron al enviar el test,
        // así que no hay que preguntarle al servidor por el par de colores predominante.
        // `discColorPair` sólo calcula qué secciones servir y no persiste nada, de modo
        // que saltearlo no altera el resultado DISC: éste se recalcula al evaluar, a
        // partir de las respuestas ya guardadas.
        const specificCompetences = test?.selectedCompetences;
        const isSpecificMode = isSpecificCompetencyMode(specificCompetences);
        const competencySections = test.sections.slice(1);

        const relevantSections = isSpecificMode
          ? selectSpecificCompetencySections(competencySections, specificCompetences)
          : selectCompetencySections(
              competencySections,
              (await CollaboratorsAPI.discColorPair(discAnswers)).data
            );

        const filteredQuestions = [];
        relevantSections.forEach((section) => {
          section.questions.forEach((q) => filteredQuestions.push(q));
        });
        // Sin preguntas no hay fase de competencias que servir: es preferible fallar acá
        // —el catch deja al colaborador en la fase DISC, pudiendo reintentar— que avanzar
        // y persistir un snapshot vacío. Se valida sobre las preguntas y no sobre las
        // secciones para cubrir también secciones sin preguntas asociadas.
        if (filteredQuestions.length === 0) {
          throw new Error(
            isSpecificMode
              ? 'No se resolvieron secciones para las competencias seleccionadas'
              : 'No se resolvieron secciones de competencias para el par de colores DISC'
          );
        }
        for (let i = filteredQuestions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [filteredQuestions[i], filteredQuestions[j]] = [filteredQuestions[j], filteredQuestions[i]];
        }
        if (filteredQuestions[0]) filteredQuestions[0].startQuestionDate = new Date();
        const startDate = new Date();
        setCompetencyQuestions(filteredQuestions);
        setStartTestDate(startDate);
        setCurrentQuestionIndex(0);
        setGlobalInitialSeconds(GLOBAL_TIME);
        setQuestionInitialSeconds(QUESTION_TIME);
        setCurrentStep(STEPS.COMPETENCY_PHASE);
        // Persiste el snapshot congelado de competencias; el servidor sella competencyStartedAt.
        persistProgress({
          competencyQuestions: filteredQuestions,
          startTestDate: startDate,
          currentQuestionIndex: 0,
          currentStep: STEPS.COMPETENCY_PHASE,
        });
        window.scrollTo(0, 0);
      } catch {
        // Genérico: en modo específicas no se llama al endpoint DISC, así que hablar
        // del "test DISC" mandaría a mirar el lugar equivocado.
        alert('Ocurrió un error al preparar la sección de competencias');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (currentStep === STEPS.COMPETENCY_PHASE) {
      if (currentQuestionIndex < competencyQuestions.length - 1) {
        if (isOptionSelected()) {
          const updatedQuestions = [...competencyQuestions];
          updatedQuestions[currentQuestionIndex].finishQuestionDate = new Date();
          updatedQuestions[currentQuestionIndex + 1].startQuestionDate = new Date();
          setCompetencyQuestions(updatedQuestions);
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          persistProgress({
            competencyQuestions: updatedQuestions,
            currentQuestionIndex: currentQuestionIndex + 1,
            currentStep: STEPS.COMPETENCY_PHASE,
          });
          window.scrollTo(0, 0);
        } else {
          alert('Por favor, selecciona una opción');
        }
      } else {
        if (isOptionSelected()) {
        const updatedQuestions = [...competencyQuestions];
        updatedQuestions[currentQuestionIndex].finishQuestionDate = new Date();
        setCompetencyQuestions(updatedQuestions);
        setCurrentStep(STEPS.INTRO_ANSWER_FUNDAMENTALS);
        persistProgress({
          competencyQuestions: updatedQuestions,
          currentStep: STEPS.INTRO_ANSWER_FUNDAMENTALS,
        });
        window.scrollTo(0, 0);
        } else {
          alert('Por favor, selecciona una opción');
          return;
        }

      }
      return;
    }

    if (currentStep === STEPS.INTRO_ANSWER_FUNDAMENTALS) {
      const questionsHighAndVeryHigh = competencyQuestions.filter((question, index) => {
        const isHighOrVeryHigh = question.options.find(
          opt => (opt.levelOption === 'Muy alto' || opt.levelOption === 'Alto') && opt.selected
        );
        if (isHighOrVeryHigh) { question.originalIndex = index; return true; }
        return false;
      });
      const questionTarget = questionsHighAndVeryHigh.length >= 2 ? questionsHighAndVeryHigh : competencyQuestions;
      let questionOneIndex = getUniqueRandomNumber(questionTarget.length);
      let questionTwoIndex = getUniqueRandomNumber(questionTarget.length, [questionOneIndex]);
      if (questionOneIndex === -1 || questionTwoIndex === -1) {
        questionOneIndex = 0;
        questionTwoIndex = questionTarget.length > 1 ? 1 : 0;
      }
      questionOneIndex = questionTarget[questionOneIndex].originalIndex ?? questionOneIndex;
      questionTwoIndex = questionTarget[questionTwoIndex].originalIndex ?? questionTwoIndex;

      const updatedQuestions = [...competencyQuestions];
      updatedQuestions[questionOneIndex].answerFundamental = '';
      updatedQuestions[questionTwoIndex].answerFundamental = '';
      const finishDate = new Date();
      setCompetencyQuestions(updatedQuestions);
      setAnswerFundamentalsIndexs([questionOneIndex, questionTwoIndex]);
      setFinishTestDate(finishDate);
      setCurrentStep(STEPS.ANSWER_FUNDAMENTALS);
      persistProgress({
        competencyQuestions: updatedQuestions,
        answerFundamentalsIndexs: [questionOneIndex, questionTwoIndex],
        finishTestDate: finishDate,
        currentStep: STEPS.ANSWER_FUNDAMENTALS,
      });
      window.scrollTo(0, 0);
      return;
    }

    if (currentStep === STEPS.ANSWER_FUNDAMENTALS) {
      const currentFundamental = competencyQuestions[answerFundamentalsIndexs[currentAnswerFundamentalsIndex]].answerFundamental;
      if (!currentFundamental || currentFundamental.length < 1) {
        alert('Por favor, fundamenta tu respuesta');
        return;
      }
      if (currentFundamental.length < 150) {
        alert('Por favor, fundamenta tu respuesta con al menos 150 caracteres');
        return;
      }
      if (currentAnswerFundamentalsIndex + 1 === MAX_ANSWER_FUNDAMENTALS) {
        submitForm();
        return;
      }
      setIsFinalStep(true);
      setCurrentAnswerFundamentalsIndex(currentAnswerFundamentalsIndex + 1);
      persistProgress({
        competencyQuestions,
        isFinalStep: true,
        currentAnswerFundamentalsIndex: currentAnswerFundamentalsIndex + 1,
        currentStep: STEPS.ANSWER_FUNDAMENTALS,
      });
      window.scrollTo(0, 0);
      return;
    }
  };

  const autoFill = () => {
    if (!AUTOFILL_ENABLED) return;

    // Fill DISC: put all options in shuffled order for every question
    const newDiscFormData = { '0': {} };
    discQuestions.forEach((question) => {
      const shuffled = question.options.map((opt) => ({ ...opt }));
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      newDiscFormData['0'][question.id] = shuffled;
    });
    setDiscFormData(newDiscFormData);

    // Fill competency: select a random option for every question
    const filledQuestions = competencyQuestions.map((question) => {
      const randomIndex = Math.floor(Math.random() * question.options.length);
      return {
        ...question,
        options: question.options.map((opt, index) => ({ ...opt, selected: index === randomIndex })),
      };
    });
    setCompetencyQuestions(filledQuestions);
  };

  const getButtonText = () => {
    if (loading) return 'Enviando...';
    if (currentStep === STEPS.TERMS) return 'Aceptar y Comenzar';
    if (currentStep === STEPS.DISC_PHASE) return 'Continuar al Test de Competencias';
    if (isFinalStep) return 'Enviar Respuestas';
    return 'Continuar';
  };

  return (
    <>
      <div className="gm-test-container">
        <div className="test-header">
          <img src={logo} alt="growme-logo" />
          {currentStep === STEPS.COMPETENCY_PHASE && (
            <TestTimer onFinishTime={onFinishTestTime} initialSeconds={globalInitialSeconds} />
          )}
        </div>

        <div className="test-main">
          <div className="test-card">
            <div className="test-card-header">
              {currentStep === STEPS.COMPETENCY_PHASE && (
                <p>Pregunta {currentQuestionIndex + 1}</p>
              )}
            </div>

            <div className="test-card-body">
              {currentStep === STEPS.TERMS && <TestTerms />}

              {currentStep === STEPS.DISC_PHASE && (
                <div className="test-section">
                  <h2>PRIMERA PARTE: AUTOPERCEPCIÓN</h2>
                  <h3>{discSection?.title}</h3>
                  <p className="intructions-p">Instrucciones</p>
                  <p className="description-p">{discSection?.description}</p>
                  {AUTOFILL_ENABLED && (
                    <button
                      type="button"
                      style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', marginBottom: '16px' }}
                      onClick={autoFill}
                    >
                      Fill All (Test)
                    </button>
                  )}
                  {discQuestions.map((question) => (
                    <SortableList
                      key={`disc-${question.id}`}
                      questionId={question.id}
                      initialAvailableItems={question.options}
                      initialDroppedItems={discFormData['0'][question.id]}
                      onSortChange={handleDiscQuestionChange}
                      numberOfDropZones={question.options.length}
                      title={question.title}
                      subtitle={question.subtitle}
                    />
                  ))}
                </div>
              )}

              {currentStep === STEPS.COMPETENCY_PHASE && (
                <>
                  {AUTOFILL_ENABLED && (
                    <button
                      type="button"
                      style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', marginBottom: '8px' }}
                      onClick={autoFill}
                    >
                      Fill All (Test)
                    </button>
                  )}
                  <Question
                    question={competencyQuestions[currentQuestionIndex]}
                  onChangeOption={onChangeOption}
                    onFinishTime={onFinishQuestionTime}
                    resetTimer={currentQuestionIndex}
                    initialSeconds={questionInitialSeconds}
                  />
                </>
              )}

              {currentStep === STEPS.INTRO_ANSWER_FUNDAMENTALS && <IntroAnswerFundamentals />}

              {currentStep === STEPS.ANSWER_FUNDAMENTALS && (
                <AnswerFundamentals
                  question={competencyQuestions[answerFundamentalsIndexs[currentAnswerFundamentalsIndex]]}
                  onChangeFundamentalInput={onChangeFundamentalInput}
                />
              )}

              {currentStep === STEPS.FINISHED && <Finished />}
            </div>

            <div className="test-card-footer">
              {currentStep !== STEPS.FINISHED && (
                <button onClick={nextAction} disabled={loading}>
                  {getButtonText()}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isFinishTimerModalOpen && (
        <NotificationModal
          icon={<img src={timerIcon} alt="timer-icon" />}
          title={modalContent.title}
          subtitle={modalContent.subtitle}
          buttonText={modalContent.buttonText}
          onClose={() => setIsFinishTimerModalOpen(false)}
        />
      )}
    </>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const iconsTerms = [
  { icon: termIcon01, text: 'Procura realizar el test en un espacio sin distracciones.' },
  { icon: termIcon02, text: 'Confía en tu primera impresión al responder.' },
  { icon: termIcon03, text: 'Responde de forma honesta y espontánea.' },
];

const TestTerms = () => (
  <>
    <div className="gm-instructions">
      <h2>TE DAMOS LA BIENVENIDA A GROWME</h2>

      <p>Este test te permitirá conocer mejor tu forma de interactuar, tomar decisiones y desenvolverte en el trabajo.</p>
      <p>A partir de tus respuestas, obtendrás una mirada sobre tu estilo de comportamiento y sobre algunas tendencias en tu manera de actuar en distintos entornos profesionales.</p>
      <p><strong>¿Cómo funciona?</strong></p>
      <p>El test está compuesto por <strong>dos secciones:</strong></p>
      <p><strong>1. Autopercepción</strong></p>
      <p>En esta parte encontrarás una serie de afirmaciones sobre formas de actuar y relacionarse.</p>
      <p>Selecciona aquellas que mejor te representen en tu día a día laboral.</p>
      <p><strong>2. Situaciones Laborales</strong></p>
      <p>Se presentarán distintos escenarios del mundo del trabajo.</p>
      <p>Tu tarea será indicar cómo actuarías frente a cada una.</p>
      <p><strong>Antes de comenzar:</strong></p>
      <p>&#10004; No existen respuestas correctas ni incorrectas.</p>
      <p>&#10004; Responde de manera honesta y espontánea.</p>
      <p>&#10004; Elige las opciones que mejor reflejen tu comportamiento habitual.</p>
    </div>
    <p className="gm-is-ready-p"><strong>¿Listo(a) para comenzar?</strong></p>
    <div className="icons-terms">
      {iconsTerms.map((item, index) => (
        <div key={index} className="icon-term">
          <img src={item.icon} alt={`icon-term-${index}`} />
          <p>{item.text}</p>
        </div>
      ))}
    </div>
  </>
);

const Question = ({ question, onChangeOption, onFinishTime, resetTimer, initialSeconds }) => (
  <>
    <h2>{question.spanish}</h2>
    <QuestionTimer onFinishTime={onFinishTime} resetTimer={resetTimer} initialSeconds={initialSeconds} />
    <div>
      {question.options.map((option, index) => (
        <div className="question-option" key={index} onClick={onChangeOption(index)}>
          <input
            type="radio"
            id={`option-${option.id}`}
            name={`question-${question.id}`}
            value={option.id}
            onChange={onChangeOption(index)}
            checked={option.selected || false}
          />
          <label htmlFor={`option-${option.id}`}>{option.spanish}</label>
        </div>
      ))}
    </div>
  </>
);

// Reloj global "tonto": cuenta hacia atrás desde initialSeconds (sembrado por el
// servidor al hidratar, o completo en flujo normal). No hace aritmética con
// timestamps absolutos, por lo que el desfase de reloj del navegador no le afecta.
const TestTimer = ({ onFinishTime, initialSeconds }) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds != null ? initialSeconds : GLOBAL_TIME);

  useEffect(() => {
    if (timeLeft <= 0) { onFinishTime(); return; }
    const id = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [timeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return (
    <div className="test-timer">
      {timeLeft > 0
        ? <p>Quedan <span>{`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}</span> para finalizar el test.</p>
        : <p>Tiempo agotado</p>}
    </div>
  );
};

// Reloj por pregunta: se siembra con initialSeconds sólo en el primer montaje (útil al
// hidratar tras recarga). En cada cambio de pregunta (resetTimer) reinicia al valor completo.
const QuestionTimer = ({ onFinishTime, resetTimer, initialSeconds }) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds != null ? initialSeconds : QUESTION_TIME);
  const prevResetRef = useRef(resetTimer);

  useEffect(() => {
    // Sólo reinicia al valor completo cuando cambia realmente de pregunta; en el
    // montaje/hidratación conserva el valor sembrado por el servidor. Comparar el
    // valor previo (en vez de un flag "ya corrí") lo hace inmune al doble efecto de
    // React.StrictMode en desarrollo.
    if (prevResetRef.current !== resetTimer) {
      prevResetRef.current = resetTimer;
      setTimeLeft(QUESTION_TIME);
    }
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(id);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resetTimer]);

  useEffect(() => {
    if (timeLeft === 0) onFinishTime();
  }, [timeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return (
    <div className="question-timer">
      <span>{`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}</span>
      <small>{timeLeft < 60 ? 'segundos' : 'minutos'}</small>
    </div>
  );
};

const IntroAnswerFundamentals = () => (
  <div className="intro-answer-fundamentals">
    <h2>Profundizando en sus decisiones</h2>
    <p>En esta sección buscamos comprender con mayor profundidad las razones que hay detrás de algunas de sus elecciones realizadas durante el test.</p>
    <p>Esta parte del test <strong>no tiene límite de tiempo por pregunta.</strong></p>
    <p>Se han seleccionado al azar algunas de sus respuestas anteriores. En cada caso, deberá explicar brevemente por qué eligió actuar de esa manera.</p>
    <p>Por ejemplo: <i>"Elegí esta alternativa porque considero importante que mis ideas sean tomadas en cuenta y que mi opinión sea percibida de manera positiva."</i></p>
    <p>Recuerde que <strong>no existen respuestas correctas o incorrectas</strong>. El objetivo de esta evaluación es comprender mejor su forma de actuar en el entorno laboral y ofrecer información que apoye su desarrollo profesional.</p>
  </div>
);

const AnswerFundamentals = ({ question, onChangeFundamentalInput }) => {
  const option = question.options.find(opt => opt.selected);
  return (
    <div className="answer-fundamentals">
      <h2>{question.spanish}</h2>
      <div className="question-option">
        <input type="radio" id={`option-${option.id}`} name={`question-${question.id}`} value={option.id} checked disabled />
        <label htmlFor={`option-${option.id}`}>{option.spanish}</label>
      </div>
      <p>Fundamenta tu respuesta (al menos 150 caracteres)</p>
      <textarea
        className="fundamental-text"
        name="fundamental-text"
        id="fundamental-text"
        value={question.answerFundamental || ''}
        onChange={onChangeFundamentalInput}
      />
      <p className="character-counter">{`${(question.answerFundamental || '').length}/700`}</p>
    </div>
  );
};

const Finished = () => (
  <div className="finished-test">
    <h2>Test finalizado</h2>
    <img src={emailIcon} alt="icon" />
    <p><strong>Growme</strong> ha sido completado correctamente.</p>
    <p>Tus resultados serán enviados a tu correo electrónico en breve.</p>
  </div>
);

const NotificationModal = ({ icon, title, subtitle, buttonText, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-icon">{icon}</div>
      <h2 className="modal-title">{title}</h2>
      <p className="modal-subtitle">{subtitle}</p>
      <button className="modal-button" onClick={onClose}>{buttonText}</button>
    </div>
  </div>
);

export default UnifiedTest;
